import { BsPersonCircle, BsCart3, BsCreditCard, BsGear, BsHeart, BsClockHistory, BsPencil, BsCheck, BsX, BsEye, BsEyeSlash } from 'react-icons/bs';
import { useUserStore } from '../stores/useUserStore';
import { useCartStore } from '../stores/useCartStore';
import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from '../lib/axios';
import LoadingSpinner from '../components/LoadingSpinner';
import OrderHistory from '../components/OrderHistory';

const UserProfilePage = () => {
  const { user, checkAuth } = useUserStore();
  const { cart } = useCartStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // State for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    personalDetails: {
      israeliID: '',
      phoneNumber: '',
      address: {
        street: '',
        houseNumber: '',
        city: '',
        postalCode: ''
      }
    }
  });

  // State for password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Initialize edit form when user data is loaded
  useEffect(() => {
    if (user) {
      setEditForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        personalDetails: {
          israeliID: user.personalDetails?.israeliID || '',
          phoneNumber: user.personalDetails?.phoneNumber || '',
          address: {
            street: user.personalDetails?.address?.street || '',
            houseNumber: user.personalDetails?.address?.houseNumber || '',
            city: user.personalDetails?.address?.city || '',
            postalCode: user.personalDetails?.address?.postalCode || ''
          }
        }
      });
    }
  }, [user]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form
      setEditForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        personalDetails: {
          israeliID: user.personalDetails?.israeliID || '',
          phoneNumber: user.personalDetails?.phoneNumber || '',
          address: {
            street: user.personalDetails?.address?.street || '',
            houseNumber: user.personalDetails?.address?.houseNumber || '',
            city: user.personalDetails?.address?.city || '',
            postalCode: user.personalDetails?.address?.postalCode || ''
          }
        }
      });
    }
    setIsEditing(!isEditing);
  };

  const handleProfileUpdate = async () => {
    if (!editForm.firstName || !editForm.lastName) {
      toast.error('שם פרטי ושם משפחה נדרשים');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put('/auth/profile', editForm);
      
      if (response.data.success) {
        toast.success('הפרופיל עודכן בהצלחה!');
        setIsEditing(false);
        // Update user in store
        await checkAuth();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון הפרופיל');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('כל השדות נדרשים');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('הסיסמאות החדשות אינן תואמות');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('הסיסמה החדשה חייבת להכיל לפחות 6 תווים');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      if (response.data.success) {
        toast.success('הסיסמה שונתה בהצלחה!');
        setShowPasswordForm(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשינוי הסיסמה');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20 pb-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* כותרת ראשית */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#9a624b] via-[#ac609c] to-[#9a624b] bg-clip-text text-transparent mb-4">
            הפרופיל שלי
          </h1>
          <p className="text-gray-600 text-xl">
            ברוך הבא {user.firstName}, נהל את החשבון והפרטים שלך
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#9a624b] to-[#ac609c] mx-auto rounded-full mt-6"></div>
        </div>

        {/* פרטי משתמש ראשיים */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-12 border border-gray-200">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#9B8A61] mb-6">
                {user.firstName} {user.lastName}
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  <strong className="text-[#D4AF37]">אימייל:</strong> {user.email}
                </p>
                <p>
                  <strong className="text-[#D4AF37]">תאריך הצטרפות:</strong> {new Date(user.createdAt).toLocaleDateString('he-IL')}
                </p>
                <p>
                  <strong className="text-[#D4AF37]">פריטים בעגלה:</strong> {cart.length}
                </p>
                <p>
                  <strong className="text-[#D4AF37]">רמת אבטחה:</strong> 
                  <span className="text-green-600 mr-2">בטוח</span>
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className="w-64 h-64 bg-blue-600 rounded-full mx-auto flex items-center justify-center mb-6">
                <BsPersonCircle className="w-32 h-32 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#9B8A61] mb-2">{user.firstName} {user.lastName}</h3>
              <p className="text-gray-700">{user.role === 'admin' ? 'מנהל מערכת' : 'לקוח'}</p>
              {user.isEmailVerified && (
                <span className="inline-block bg-brand-primary text-white px-3 py-1 rounded-full text-sm mt-2">
                  אימייל מאומת ✓
                </span>
              )}
            </div>
          </div>
        </div>

        {/* תפריט טאבים */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-12 border border-gray-200">
          <div className="flex flex-wrap gap-4 mb-8 border-b border-gray-200 pb-4">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                activeTab === 'profile'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BsPersonCircle />
              פרטים אישיים
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                activeTab === 'orders'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BsClockHistory />
              היסטוריית הזמנות
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                activeTab === 'settings'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BsGear />
              הגדרות ואבטחה
            </button>
          </div>

          {/* תוכן הטאבים */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-[#9B8A61]">פרטים אישיים</h3>
                <button
                  onClick={handleEditToggle}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-300"
                >
                  {isEditing ? <BsX /> : <BsPencil />}
                  {isEditing ? 'ביטול' : 'עריכה'}
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-[#D4AF37] mb-2">שם פרטי</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={isEditing ? editForm.firstName : user.firstName}
                    onChange={(e) => isEditing && setEditForm({...editForm, firstName: e.target.value})}
                    readOnly={!isEditing}
                    className={`w-full p-3 border rounded-lg text-gray-700 ${
                      isEditing 
                        ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                        : 'bg-gray-100 border-gray-300'
                    }`}
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-[#D4AF37] mb-2">שם משפחה</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={isEditing ? editForm.lastName : user.lastName}
                    onChange={(e) => isEditing && setEditForm({...editForm, lastName: e.target.value})}
                    readOnly={!isEditing}
                    className={`w-full p-3 border rounded-lg text-gray-700 ${
                      isEditing 
                        ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                        : 'bg-gray-100 border-gray-300'
                    }`}
                    autoComplete="family-name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[#D4AF37] mb-2">אימייל</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={user.email}
                    readOnly
                    className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700"
                    autoComplete="email"
                  />
                  <p className="text-sm text-gray-600 mt-1">לא ניתן לשנות אימייל</p>
                </div>
                <div>
                  <label htmlFor="phoneNumber" className="block text-[#D4AF37] mb-2">מספר טלפון</label>
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={isEditing ? editForm.personalDetails.phoneNumber : (user.personalDetails?.phoneNumber || '')}
                    onChange={(e) => isEditing && setEditForm({
                      ...editForm, 
                      personalDetails: {...editForm.personalDetails, phoneNumber: e.target.value}
                    })}
                    readOnly={!isEditing}
                    placeholder="0533332127"
                    className={`w-full p-3 border rounded-lg text-gray-700 ${
                      isEditing 
                        ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                        : 'bg-gray-100 border-gray-300'
                    }`}
                    autoComplete="tel"
                  />
                </div>
              </div>

              {/* פרטים ישראליים */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-xl font-bold text-[#9B8A61] mb-4">פרטים נדרשים לתשלומים</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="israeliID" className="block text-[#D4AF37] mb-2">תעודת זהות</label>
                    <input
                      id="israeliID"
                      name="israeliID"
                      type="text"
                      value={isEditing ? editForm.personalDetails.israeliID : (user.personalDetails?.israeliID || '')}
                      onChange={(e) => isEditing && setEditForm({
                        ...editForm, 
                        personalDetails: {...editForm.personalDetails, israeliID: e.target.value}
                      })}
                      readOnly={!isEditing}
                      placeholder="123456789"
                      maxLength={9}
                      className={`w-full p-3 border rounded-lg text-gray-700 ${
                        isEditing 
                          ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                          : 'bg-gray-100 border-gray-300'
                      }`}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-[#D4AF37] mb-2">עיר</label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      value={isEditing ? editForm.personalDetails.address.city : (user.personalDetails?.address?.city || '')}
                      onChange={(e) => isEditing && setEditForm({
                        ...editForm, 
                        personalDetails: {
                          ...editForm.personalDetails, 
                          address: {...editForm.personalDetails.address, city: e.target.value}
                        }
                      })}
                      readOnly={!isEditing}
                      placeholder="תל אביב"
                      className={`w-full p-3 border rounded-lg text-gray-700 ${
                        isEditing 
                          ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                          : 'bg-gray-100 border-gray-300'
                      }`}
                      autoComplete="address-level2"
                    />
                  </div>
                  <div>
                    <label htmlFor="street" className="block text-[#D4AF37] mb-2">רחוב</label>
                    <input
                      id="street"
                      name="street"
                      type="text"
                      value={isEditing ? editForm.personalDetails.address.street : (user.personalDetails?.address?.street || '')}
                      onChange={(e) => isEditing && setEditForm({
                        ...editForm, 
                        personalDetails: {
                          ...editForm.personalDetails, 
                          address: {...editForm.personalDetails.address, street: e.target.value}
                        }
                      })}
                      readOnly={!isEditing}
                      placeholder="רחוב הרצל"
                      className={`w-full p-3 border rounded-lg text-gray-700 ${
                        isEditing 
                          ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                          : 'bg-gray-100 border-gray-300'
                      }`}
                      autoComplete="street-address"
                    />
                  </div>
                  <div>
                    <label htmlFor="houseNumber" className="block text-[#D4AF37] mb-2">מספר בית</label>
                    <input
                      id="houseNumber"
                      name="houseNumber"
                      type="text"
                      value={isEditing ? editForm.personalDetails.address.houseNumber : (user.personalDetails?.address?.houseNumber || '')}
                      onChange={(e) => isEditing && setEditForm({
                        ...editForm, 
                        personalDetails: {
                          ...editForm.personalDetails, 
                          address: {...editForm.personalDetails.address, houseNumber: e.target.value}
                        }
                      })}
                      readOnly={!isEditing}
                      placeholder="123"
                      className={`w-full p-3 border rounded-lg text-gray-700 ${
                        isEditing 
                          ? 'bg-gray-50 border-blue-300 focus:border-[#D4AF37]' 
                          : 'bg-gray-100 border-gray-300'
                      }`}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleProfileUpdate}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-300 disabled:opacity-50"
                  >
                    <BsCheck />
                    {loading ? 'שומר...' : 'שמור שינויים'}
                  </button>
                  <button
                    onClick={handleEditToggle}
                    className="flex items-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all duration-300"
                  >
                    <BsX />
                    ביטול
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <OrderHistory />
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-[#9B8A61] mb-4">הגדרות ואבטחה</h3>
              
              {/* שינוי סיסמה */}
              <div className="bg-gray-100 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="text-[#D4AF37] font-medium text-lg">שינוי סיסמה</h4>
                    <p className="text-gray-600 text-sm">שמור על החשבון שלך בטוח</p>
                  </div>
                  <button
                    onClick={() => setShowPasswordForm(!showPasswordForm)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-300"
                  >
                    {showPasswordForm ? 'ביטול' : 'שנה סיסמה'}
                  </button>
                </div>

                {showPasswordForm && (
                  <div className="space-y-4 border-t border-gray-300 pt-4">
                    <div>
                      <label htmlFor="currentPassword" className="block text-[#D4AF37] mb-2">סיסמה נוכחית</label>
                      <div className="relative">
                        <input
                          id="currentPassword"
                          name="currentPassword"
                          type={showPasswords.current ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                          className="w-full p-3 bg-gray-50 border border-blue-300 rounded-lg text-gray-700 pr-10"
                          placeholder="הכנס סיסמה נוכחית"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-blue-600"
                        >
                          {showPasswords.current ? <BsEyeSlash /> : <BsEye />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="newPassword" className="block text-[#D4AF37] mb-2">סיסמה חדשה</label>
                      <div className="relative">
                        <input
                          id="newPassword"
                          name="newPassword"
                          type={showPasswords.new ? 'text' : 'password'}
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                          className="w-full p-3 bg-gray-50 border border-blue-300 rounded-lg text-gray-700 pr-10"
                          placeholder="הכנס סיסמה חדשה (לפחות 6 תווים)"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-blue-600"
                        >
                          {showPasswords.new ? <BsEyeSlash /> : <BsEye />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="confirmPassword" className="block text-[#D4AF37] mb-2">אישור סיסמה חדשה</label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showPasswords.confirm ? 'text' : 'password'}
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                          className="w-full p-3 bg-gray-50 border border-blue-300 rounded-lg text-gray-700 pr-10"
                          placeholder="הכנס שוב את הסיסמה החדשה"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-blue-600"
                        >
                          {showPasswords.confirm ? <BsEyeSlash /> : <BsEye />}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={handlePasswordChange}
                      disabled={loading}
                      className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-300 disabled:opacity-50"
                    >
                      {loading ? 'משנה סיסמה...' : 'שמור סיסמה חדשה'}
                    </button>
                  </div>
                )}
              </div>

              {/* מידע אבטחה */}
              <div className="bg-gray-100 rounded-lg p-6">
                <h4 className="text-[#D4AF37] font-medium text-lg mb-4">מידע אבטחה</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">שינוי סיסמה אחרון:</span>
                    <span className="text-gray-700">
                      {user.security?.lastPasswordChange 
                        ? new Date(user.security.lastPasswordChange).toLocaleDateString('he-IL')
                        : 'לא זמין'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">הצפנת נתונים:</span>
                    <span className="text-green-600">פעילה</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">אימות דו-שלבי:</span>
                    <span className="text-gray-600">לא זמין (בקרוב)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* סטטיסטיקות מהירות */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-2xl p-6 text-center border border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105">
            <div className="w-16 h-16 bg-gradient-to-r from-[#9B8A61] to-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <BsCart3 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#9B8A61] mb-2">פריטים בעגלה</h3>
            <p className="text-gray-700">{cart.length} פריטים</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-2xl p-6 text-center border border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105">
            <div className="w-16 h-16 bg-gradient-to-r from-[#9B8A61] to-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <BsClockHistory className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#9B8A61] mb-2">הזמנות</h3>
            <p className="text-gray-700">0 הזמנות</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-2xl p-6 text-center border border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105">
            <div className="w-16 h-16 bg-gradient-to-r from-[#9B8A61] to-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <BsHeart className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#9B8A61] mb-2">מועדפים</h3>
            <p className="text-gray-700">{user.wishlist?.length || 0} מוצרים</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-2xl p-6 text-center border border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105">
            <div className="w-16 h-16 bg-gradient-to-r from-[#9B8A61] to-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <BsCreditCard className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#9B8A61] mb-2">רמת אבטחה</h3>
            <p className="text-green-600">גבוהה</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage; 